import api from 'api'
import {
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
  CACHE_TAG_MASTER_ROUTE_META,
  CACHE_TAG_MASTER_ROUTE_VERSIONS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTE_STOP_LISTS,
} from 'api/cacheTagTypes'

import type {
  MasterSlotAssignBody,
  MasterSlotChangeDayBody,
  MasterSlotDetailsBody,
  MasterSlotDetailsResponse,
  MasterSlotSequenceBody,
  MasterSlotSequenceResponse,
  MasterSlotUnassignBody,
  RestoreMasterSlotPreviewResponse,
  RestoreMasterSlotVersionBody,
  RouteSlotCountResponse,
  SyncDailyToMasterSlotBody,
  SyncDailyToMasterSlotResponse,
} from 'types/api/route/routeSlotRequests'

interface RouteIdArg {
  routeId: string
}

interface SkipInvalidateArg {
  skipInvalidate?: boolean
}

interface SetMasterSlotSequenceArgs extends RouteIdArg, SkipInvalidateArg {
  data: MasterSlotSequenceBody
}

interface AssignMasterSlotsArgs extends RouteIdArg, SkipInvalidateArg {
  data: MasterSlotAssignBody
}

interface UnassignMasterSlotsArgs extends RouteIdArg, SkipInvalidateArg {
  data: MasterSlotUnassignBody
}

interface ChangeMasterSlotDayOfWeekArgs extends RouteIdArg {
  data: MasterSlotChangeDayBody
}

interface GetMasterSlotDetailsArgs extends RouteIdArg {
  includeTags?: boolean
  includeContainers?: boolean
  data: MasterSlotDetailsBody
}

interface RestoreMasterSlotVersionArgs extends RouteIdArg, SkipInvalidateArg {
  data: RestoreMasterSlotVersionBody
}

interface SyncMasterSlotDailyToMasterArgs extends RouteIdArg, SkipInvalidateArg {
  data: SyncDailyToMasterSlotBody
}

// Mirrors LIVE_PUBLISH_INVALIDATION_TAGS in masterRoute.ts.
const MASTER_SLOT_INVALIDATION_TAGS = [
  CACHE_TAG_MASTER_ROUTE_META,
  CACHE_TAG_MASTER_ROUTE_VERSIONS,
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_GEOJSON,
] as const

// Mirrors masterRoute.ts's v2 skipInvalidate contract.
const masterSlotInvalidatesTags = (_result: unknown, _error: unknown, arg: SkipInvalidateArg | undefined): readonly string[] =>
  arg?.skipInvalidate ? [] : [...MASTER_SLOT_INVALIDATION_TAGS]

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    setMasterSlotSequence: builder.mutation<MasterSlotSequenceResponse, SetMasterSlotSequenceArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/master/stops/sequence`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: masterSlotInvalidatesTags,
    }),
    assignMasterSlots: builder.mutation<RouteSlotCountResponse, AssignMasterSlotsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/master/stops/assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: masterSlotInvalidatesTags,
    }),
    unassignMasterSlots: builder.mutation<RouteSlotCountResponse, UnassignMasterSlotsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/master/stops/unassign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: masterSlotInvalidatesTags,
    }),
    changeMasterSlotDayOfWeek: builder.mutation<RouteSlotCountResponse, ChangeMasterSlotDayOfWeekArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/master/stops/day-of-week`,
        method: 'POST',
        body: data,
      }),
      // Matches v2's changeDayOfWeekMasterStops, which also declares no invalidation here.
      invalidatesTags: [],
    }),
    getMasterSlotDetails: builder.query<MasterSlotDetailsResponse, GetMasterSlotDetailsArgs>({
      query: ({ routeId, includeTags = true, includeContainers = false, data }) => ({
        url: `/api/v3/route/${routeId}/master/stops/details`,
        method: 'POST',
        params: { includeTags, includeContainers },
        body: data,
      }),
      providesTags: [CACHE_TAG_ROUTES_STOPS, CACHE_TAG_ROUTE_STOP_LISTS],
    }),
    restoreMasterSlotVersion: builder.mutation<RouteSlotCountResponse, RestoreMasterSlotVersionArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/master/stops/restore`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: masterSlotInvalidatesTags,
    }),
    // Preview is read-only — same body as the committing mutation, no cache invalidation.
    previewRestoreMasterSlotVersion: builder.mutation<RestoreMasterSlotPreviewResponse, RestoreMasterSlotVersionArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/master/stops/restore/preview`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [],
    }),
    syncMasterSlotDailyToMaster: builder.mutation<SyncDailyToMasterSlotResponse, SyncMasterSlotDailyToMasterArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v3/route/${routeId}/master/stops/sync-daily`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: masterSlotInvalidatesTags,
    }),
  }),
})

export const {
  useSetMasterSlotSequenceMutation,
  useAssignMasterSlotsMutation,
  useUnassignMasterSlotsMutation,
  useChangeMasterSlotDayOfWeekMutation,
  useLazyGetMasterSlotDetailsQuery,
  useRestoreMasterSlotVersionMutation,
  usePreviewRestoreMasterSlotVersionMutation,
  useSyncMasterSlotDailyToMasterMutation,
} = extendedApi
